import { computed } from "vue";
import { createI18n } from "vue-i18n";
import { DateTime, Settings } from "luxon";
import envVars from "@/helpers/Env";

import messagesDE from "@/locales/de.json";
import messagesFR from "@/locales/fr.json";
import messagesEN from "@/locales/en.json";
import messagesES from "@/locales/es.json";

import { useUser } from "@/api/user.store";
import type { NumberFormatOptions } from '@intlify/core-base';
import type { ValidLanguage } from "./i18n/ValidLanguages";

const messages = {
  de: messagesDE,
  fr: messagesFR,
  en: messagesEN,
  es: messagesES
};

const decimalNumberFormat: NumberFormatOptions = {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
};
// The point format is used when points that are
// added or substracted are displayed:
const pointNumberFormat: NumberFormatOptions = {
  style: "decimal" as const,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: "exceptZero" as const
};
const flexiblePointNumberFormat: NumberFormatOptions = {
  style: "decimal" as const,
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  signDisplay: "exceptZero" as const
};
const displaySignNumberFormat: NumberFormatOptions = {
  signDisplay: "always" as const
};
const valueNumberFormat: NumberFormatOptions = {
  style: "decimal" as const,
  maximumFractionDigits: 2
};
const standardNumberFormat: NumberFormatOptions = {
  style: "decimal" as const,
  minimumFractionDigits: 0,
  maximumFractionDigits: 5
};
const standardNumberFormats = {
  standard: standardNumberFormat,
  value: valueNumberFormat,
  decimal: decimalNumberFormat,
  points: pointNumberFormat,
  pointsFlexible: flexiblePointNumberFormat,
  displaySign: displaySignNumberFormat
};

const vuei18n = createI18n({
  legacy: false,
  numberFormats: {
    de: standardNumberFormats,
    en: standardNumberFormats,
    fr: standardNumberFormats,
    es: standardNumberFormats
  },
  globalInjection: true,
  locale: envVars.locale || "de",
  fallbackLocale: envVars.fallbackLocale || "de",
  messages
});

export const i18n = vuei18n.global;

// LOAD LOCALE

// If there's a language query, take that first!
const querystring = window.location.search;
const urlParams = new URLSearchParams(querystring);
const queryLanguage = urlParams.get("language");
if (queryLanguage && queryLanguage in messages) {
  console.debug("locale loaded from query string:", queryLanguage);
  i18n.locale.value = queryLanguage as ValidLanguage;
}
// No language query?
else {
  // load selected localed from localStorage
  const lastLocale = localStorage.getItem("language") as ValidLanguage | null;
  if (lastLocale != null && i18n.availableLocales.includes(lastLocale)) {
    console.debug("locale loaded from memory:", lastLocale);
    // choose language if there's a translation for that language
    i18n.locale.value = lastLocale;
  }

  // No locale in memory? Set to browser default.
  else {
    const locale = window.navigator.language.split("-")[0] as ValidLanguage;

    // choose language if there's a translation for that language
    if (i18n.availableLocales.includes(locale)) {
      i18n.locale.value = locale;
      console.debug("loaded locale by navigator default");
    } else {
      i18n.locale.value = "en";
    }
  }
}

document.documentElement.lang = i18n.locale.value;

/**
 * Change the locale setting
 */
export function setLocale(locale: ValidLanguage, saveOnline = true) {
  i18n.locale.value = locale;
  localStorage.setItem("language", locale);

  document.documentElement.lang = locale;
  Settings.defaultLocale = locale;

  if (saveOnline) {
    const { updateUser, user } = useUser();
    // only update the user if there's a user logged in:
    if (user.value != null) {
      updateUser({
        language: locale
      }).catch((error) => {
        throw error;
      });
    }
  }
}

export function formatDate(date: Date, formatString = "DDD") {
  const dateTime = DateTime.fromJSDate(date);
  return dateTime.setLocale(i18n.locale.value).toFormat(formatString);
}

export function formatDateString(dateString: string, formatString = "DDD") {
  const dateTime = DateTime.fromISO(dateString);
  return dateTime.setLocale(i18n.locale.value).toFormat(formatString);
}

/**
 *
 * @param date
 * @param _extra The extra parameter is used to force a re-render when the time changes.
 */
export function formatDateRelative(date: Date, _extra?: { now: unknown }) {
  const dateTime = DateTime.fromJSDate(date);
  return dateTime.setLocale(i18n.locale.value).toRelative({
    unit: ["days", "hours", "minutes", "seconds"]
  });
}
export function formatDateStringRelative(dateString: string, _extra?: { now: unknown }) {
  const dateTime = DateTime.fromISO(dateString);
  return dateTime.setLocale(i18n.locale.value).toRelative({
    unit: ["days", "hours", "minutes", "seconds"]
  });
}

const ordinalSuffixes = {
  en: new Map([
    ["one", "st"],
    ["two", "nd"],
    ["few", "rd"],
    ["other", "th"]
  ]),
  de: new Map([
    ["one", "."],
    ["other", "."]
  ]),
  es: new Map([
    ["one", "a"],
    ["two", "a"],
    ["few", "a"],
    ["other", "a"]
  ]),
  fr: new Map([
    ["one", "er"],
    ["other", "ème"]
  ])
};

export function getOrdinalNumber(number: number) {
  const ordinalRules = new Intl.PluralRules(i18n.locale.value, {
    type: "ordinal"
  });
  const category = ordinalRules.select(number);

  const suffix = ordinalSuffixes[i18n.locale.value].get(category);
  return number + (suffix ?? "");
}

export default vuei18n;

// Hot updates
if (import.meta.hot) {
  import.meta.hot.accept(
    [
      "../src/locales/en.json",
      "../src/locales/fr.json",
      "../src/locales/es.json",
      "../src/locales/de.json"
    ],
    ([en, fr, es, de]) => {
      if (en) {
        i18n.setLocaleMessage("en", {
          ...(en as object),
          ":": "@:signs.colon"
        } as typeof messagesEN);
      }
      if (fr) {
        i18n.setLocaleMessage("fr", {
          ...(fr as object),
          ":": "@:signs.colon"
        } as typeof messagesFR);
      }
      if (es) {
        i18n.setLocaleMessage("es", {
          ...(es as object),
          ":": "@:signs.colon"
        } as typeof messagesES);
      }
      if (de) {
        i18n.setLocaleMessage("de", {
          ...(de as object),
          ":": "@:signs.colon"
        } as typeof messagesDE);
      }
    }
  );
}

export const andListJoiner = computed(() => {
  return new Intl.ListFormat(i18n.locale.value, {
    style: "long",
    type: "conjunction"
  });
});

export const orListJoiner = computed(() => {
  return new Intl.ListFormat(i18n.locale.value, {
    style: "long",
    type: "disjunction"
  });
});

export function joinList(items: string[], joiner: "and" | "or") {
  if (joiner == "and") return andListJoiner.value.format(items);
  if (joiner == "or") return orListJoiner.value.format(items);
  console.warn("joiner not correct", joiner);
  return items.join("");
}
